import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { Link, type LinkProps } from "./Link";

const styles = {
	base: [
		// Base
		"relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold cursor-pointer",
		// Sizing
		"px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
		// Focus
		"focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary",
		// Disabled
		"data-disabled:opacity-50 data-disabled:cursor-default",
		// Icon
		"*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
	],
	solid: [
		// Optical border, implemented as the button background to avoid corner artifacts
		"border-transparent bg-(--btn-border)",
		// Dark mode: border is rendered on `after` so background is set to button background
		"dark:bg-(--btn-bg)",
		// Button background, implemented as foreground layer to stack on top of pseudo-border layer
		"before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)",
		// Drop shadow, applied to the inset `before` layer so it blends with the border
		"before:shadow-sm",
		// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
		"dark:before:hidden",
		// Dark mode: Subtle white outline is applied using a border
		"dark:border-white/5",
		// Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
		"after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]",
		// Inner highlight shadow
		"after:shadow-[inset_0_1px_--theme(--color-white/15%)]",
		// White overlay on hover
		"data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)",
		// Dark mode: `after` layer expands to cover entire button
		"dark:after:-inset-px dark:after:rounded-lg",
		// Disabled
		"data-disabled:before:shadow-none data-disabled:after:shadow-none",
	],
	outline: [
		// Base
		"border-base-content/10 text-base-content data-active:bg-base-content/5 data-hover:bg-base-content/5",
		// Dark mode
		"dark:border-base-content/15 dark:text-base-content dark:[--btn-bg:transparent] dark:data-active:bg-base-content/5 dark:data-hover:bg-base-content/5",
		// Icon
		"[--btn-icon:var(--color-base-content)]/50 data-active:[--btn-icon:var(--color-base-content)]/70 data-hover:[--btn-icon:var(--color-base-content)]/70",
	],
	plain: [
		// Base
		"border-transparent text-base-content data-active:bg-base-content/5 data-hover:bg-base-content/5",
		// Dark mode
		"dark:text-base-content dark:data-active:bg-base-content/10 dark:data-hover:bg-base-content/10",
		// Icon
		"[--btn-icon:var(--color-base-content)]/50 data-active:[--btn-icon:var(--color-base-content)]/70 data-hover:[--btn-icon:var(--color-base-content)]/70",
	],
	colors: {
		"dark/zinc": [
			"text-primary-content [--btn-bg:var(--color-primary)] [--btn-border:var(--color-primary)]/90 [--btn-hover-overlay:var(--color-base-content)]/10",
			"dark:text-primary-content dark:[--btn-bg:var(--color-primary)] dark:[--btn-hover-overlay:var(--color-base-content)]/5",
			"[--btn-icon:var(--color-primary-content)]/60 data-active:[--btn-icon:var(--color-primary-content)]/80 data-hover:[--btn-icon:var(--color-primary-content)]/80",
		],
		light: [
			"text-base-content [--btn-bg:var(--color-base-100)] [--btn-border:var(--color-base-content)]/10 [--btn-hover-overlay:var(--color-base-content)]/5 data-active:[--btn-border:var(--color-base-content)]/15 data-hover:[--btn-border:var(--color-base-content)]/15",
			"dark:text-base-content dark:[--btn-hover-overlay:var(--color-base-content)]/5 dark:[--btn-bg:var(--color-base-200)]",
			"[--btn-icon:var(--color-base-content)]/50 data-active:[--btn-icon:var(--color-base-content)]/70 data-hover:[--btn-icon:var(--color-base-content)]/70",
		],
		"dark/white": [
			"text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
			"dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
			"[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
		],
		dark: [
			"text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
			"dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
			"[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
		],
		white: [
			"text-base-content [--btn-bg:var(--color-base-100)] [--btn-border:var(--color-base-content)]/10 [--btn-hover-overlay:var(--color-base-content)]/5 data-active:[--btn-border:var(--color-base-content)]/15 data-hover:[--btn-border:var(--color-base-content)]/15",
			"dark:[--btn-hover-overlay:var(--color-base-content)]/5",
			"[--btn-icon:var(--color-base-content)]/50 data-active:[--btn-icon:var(--color-base-content)]/70 data-hover:[--btn-icon:var(--color-base-content)]/70",
		],
		zinc: [
			"text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90",
			"dark:[--btn-hover-overlay:var(--color-white)]/5",
			"[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
		],
		indigo: [
			"text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90",
			"[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]",
		],
		cyan: [
			"text-cyan-950 [--btn-bg:var(--color-info)] [--btn-border:var(--color-info)]/80 [--btn-hover-overlay:var(--color-base-100)]/25",
			"[--btn-icon:var(--color-info-content)]",
		],
		red: [
			"text-error-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-error)] [--btn-border:var(--color-error)]/90",
			"[--btn-icon:var(--color-error-content)]/60 data-active:[--btn-icon:var(--color-error-content)]/80 data-hover:[--btn-icon:var(--color-error-content)]/80",
		],
		orange: [
			"text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-orange-500)] [--btn-border:var(--color-orange-600)]/90",
			"[--btn-icon:var(--color-orange-300)] data-active:[--btn-icon:var(--color-orange-200)] data-hover:[--btn-icon:var(--color-orange-200)]",
		],
		amber: [
			"text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80",
			"[--btn-icon:var(--color-amber-600)]",
		],
		yellow: [
			"text-yellow-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-yellow-300)] [--btn-border:var(--color-yellow-400)]/80",
			"[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)]",
		],
		lime: [
			"text-lime-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-lime-300)] [--btn-border:var(--color-lime-400)]/80",
			"[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)]",
		],
		green: [
			"text-success-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-success)] [--btn-border:var(--color-success)]/90",
			"[--btn-icon:var(--color-success-content)]/60 data-active:[--btn-icon:var(--color-success-content)]/80 data-hover:[--btn-icon:var(--color-success-content)]/80",
		],
		emerald: [
			"text-success-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-success)] [--btn-border:var(--color-success)]/90",
			"[--btn-icon:var(--color-success-content)]/60 data-active:[--btn-icon:var(--color-success-content)]/80 data-hover:[--btn-icon:var(--color-success-content)]/80",
		],
		teal: [
			"text-success-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-success)] [--btn-border:var(--color-success)]/90",
			"[--btn-icon:var(--color-success-content)]/60 data-active:[--btn-icon:var(--color-success-content)]/80 data-hover:[--btn-icon:var(--color-success-content)]/80",
		],
		sky: [
			"text-info-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-info)] [--btn-border:var(--color-info)]/80",
			"[--btn-icon:var(--color-info-content)]/60 data-active:[--btn-icon:var(--color-info-content)]/80 data-hover:[--btn-icon:var(--color-info-content)]/80",
		],
		blue: [
			"text-primary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-primary)] [--btn-border:var(--color-primary)]/90",
			"[--btn-icon:var(--color-primary-content)]/60 data-active:[--btn-icon:var(--color-primary-content)]/80 data-hover:[--btn-icon:var(--color-primary-content)]/80",
		],
		violet: [
			"text-primary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-primary)] [--btn-border:var(--color-primary)]/90",
			"[--btn-icon:var(--color-primary-content)]/60 data-active:[--btn-icon:var(--color-primary-content)]/80 data-hover:[--btn-icon:var(--color-primary-content)]/80",
		],
		purple: [
			"text-primary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-primary)] [--btn-border:var(--color-primary)]/90",
			"[--btn-icon:var(--color-primary-content)]/60 data-active:[--btn-icon:var(--color-primary-content)]/80 data-hover:[--btn-icon:var(--color-primary-content)]/80",
		],
		fuchsia: [
			"text-secondary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-secondary)] [--btn-border:var(--color-secondary)]/90",
			"[--btn-icon:var(--color-secondary-content)]/60 data-active:[--btn-icon:var(--color-secondary-content)]/80 data-hover:[--btn-icon:var(--color-secondary-content)]/80",
		],
		pink: [
			"text-secondary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-secondary)] [--btn-border:var(--color-secondary)]/90",
			"[--btn-icon:var(--color-secondary-content)]/60 data-active:[--btn-icon:var(--color-secondary-content)]/80 data-hover:[--btn-icon:var(--color-secondary-content)]/80",
		],
		rose: [
			"text-secondary-content [--btn-hover-overlay:var(--color-base-100)]/10 [--btn-bg:var(--color-secondary)] [--btn-border:var(--color-secondary)]/90",
			"[--btn-icon:var(--color-secondary-content)]/60 data-active:[--btn-icon:var(--color-secondary-content)]/80 data-hover:[--btn-icon:var(--color-secondary-content)]/80",
		],
	},
};

type ButtonStyleProps =
	| { color?: keyof typeof styles.colors; outline?: never; plain?: never }
	| { color?: never; outline: true; plain?: never }
	| { color?: never; outline?: never; plain: true };

type BaseButtonProps = ButtonStyleProps & {
	className?: string;
	children: React.ReactNode;
};

export type ButtonProps = BaseButtonProps &
	(
		| ({ to?: never } & Omit<Headless.ButtonProps, "as" | "className">)
		| LinkProps
	);

export const Button = forwardRef(function Button(
	{ color, outline, plain, className, children, ...props }: ButtonProps,
	ref: React.ForwardedRef<HTMLElement>,
) {
	const classes = clsx(
		className,
		styles.base,
		outline
			? styles.outline
			: plain
				? styles.plain
				: clsx(styles.solid, styles.colors[color ?? "dark/zinc"]),
	);

	return "href" in props ? (
		<Link
			{...(props as LinkProps)}
			className={classes}
			ref={ref as React.ForwardedRef<HTMLAnchorElement>}
		>
			<TouchTarget>{children}</TouchTarget>
		</Link>
	) : (
		<Headless.Button {...props} className={classes} ref={ref}>
			<TouchTarget>{children}</TouchTarget>
		</Headless.Button>
	);
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
	return (
		<>
			<span
				className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
				aria-hidden="true"
			/>
			{children}
		</>
	);
}
